import React, { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, X, UserRound } from 'lucide-react';
import { TrustedBadge } from '@/components/TrustedBadge';

const haystack = (u) => `${u.name || ''} ${u.email || ''} ${u.whatsapp || ''} ${u.mobile || ''}`.toLowerCase();

export const UserPicker = ({ users, value, onChange, testid = 'user-picker' }) => {
    const [q, setQ] = useState('');
    const [open, setOpen] = useState(false);
    const selected = users.find(u => u.id === value);
    const matches = useMemo(() => {
        const t = q.trim().toLowerCase();
        const list = t ? users.filter(u => haystack(u).includes(t)) : users;
        return [...list.filter(u => u.stock_wallet_unlocked), ...list.filter(u => !u.stock_wallet_unlocked)].slice(0, 12);
    }, [q, users]);

    if (selected) {
        return (
            <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-3 h-10" data-testid={`${testid}-selected`}>
                <UserRound className="h-4 w-4 text-[#1F9D8A] shrink-0" />
                <div className="min-w-0 text-sm leading-tight">
                    <div className="font-medium truncate flex items-center gap-1.5">{selected.name || selected.email}{selected.stock_wallet_unlocked && <TrustedBadge small label="Trusted" testid={`${testid}-selected-trusted`} />}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{selected.email}{(selected.whatsapp || selected.mobile) ? ` · ${selected.whatsapp || selected.mobile}` : ''}</div>
                </div>
                <Button type="button" variant="ghost" size="sm" className="ml-auto h-7 px-2" onClick={() => { onChange(''); setQ(''); }} data-testid={`${testid}-clear`}><X className="h-3.5 w-3.5" /></Button>
            </div>
        );
    }
    return (
        <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={e => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
                placeholder="Search name / email / WhatsApp…" className="pl-8" data-testid={`${testid}-search`} />
            {open && (
                <div className="absolute z-30 mt-1 w-full rounded-md border border-border bg-popover shadow-lg max-h-72 overflow-y-auto" data-testid={`${testid}-results`}>
                    {matches.map(u => (
                        <button type="button" key={u.id} onMouseDown={() => { onChange(u.id); setOpen(false); }}
                            className="w-full text-left px-3 py-2 hover:bg-secondary transition-colors" data-testid={`${testid}-option-${u.id}`}>
                            <div className="text-sm font-medium flex items-center gap-1.5">{u.name || u.email}{u.stock_wallet_unlocked && <TrustedBadge small label="Trusted" testid={`${testid}-trusted-${u.id}`} />}</div>
                            <div className="text-[11px] text-muted-foreground">{u.email}{(u.whatsapp || u.mobile) ? ` · ${u.whatsapp || u.mobile}` : ''}</div>
                        </button>
                    ))}
                    {matches.length === 0 && <div className="px-3 py-3 text-xs text-muted-foreground">No users match.</div>}
                </div>
            )}
        </div>
    );
};
